import React from 'react';
import { css, keyframes } from '@emotion/css';
import { Box, Typography, useTheme } from '@mui/material';
import { truncateText } from '@utils/truncateText';
import { useTranslation } from 'react-i18next';

interface LoaderProps {
  text?: string;
  maxLength?: number;
  mt?: string;
  mb?: string;
  minHeight?: string;
}

const Loader: React.FC<LoaderProps> = ({
  text,
  maxLength,
  mt = '2.8rem',
  mb = '4rem',
  minHeight,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const animation = keyframes`
    0% {
      background-position: 0 0, 0 100%, 100% 100%;
    }
    25% {
      background-position: 100% 0, 0 100%, 100% 100%;
    }
    50% {
      background-position: 100% 0, 0 0, 100% 100%;
    }
    75% {
      background-position: 100% 0, 0 0, 0 100%;
    }
    100% {
      background-position: 100% 100%, 0 0, 0 100%;
    }
  `;

  const styles = {
    root: css`
      width: 100%;
      display: flex;
      flex-direction: column !important;
      place-content: center;
      align-items: center;
      gap: 2rem;
      margin-bottom: ${mb};
      margin-top: ${mt};
      min-height: ${minHeight};
    `,
    loader: css`
      width: 90px;
      aspect-ratio: 1;
      --g1: conic-gradient(
        ${theme.palette.accent.main},
        ${theme.palette.accent.main} 0
      );
      background: var(--g1), var(--g1), var(--g1), var(--g1), var(--g1),
        var(--g1);
      background-size: 40px 40px;
      background-repeat: no-repeat;
      animation: ${animation} 1.5s infinite;
    `,
    text: css`
      text-align: center;
      font-size: 1.2rem;
    `,
  };

  return (
    <Box className={styles.root}>
      <Typography component="span" className={styles.loader} />
      <Typography className={styles.text} variant="h6" sx={{ mb: 4 }}>
        {text
          ? truncateText({ textValue: text, maxTextLength: maxLength })
          : t('handling_request_please_wait')}
      </Typography>
    </Box>
  );
};

export default Loader;
